import parse from 'html-react-parser'
import {
  type IColumn,
  IconButton,
  type IRow,
  Tooltip,
  NameValueRecord,
  type INameValueRecordProps,
} from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { DayjsFormats, getLabelAsNumberInternationalFormat } from 'shared/lib'
import { getCellForNameDefault } from 'shared/helpers/getCellForNameDefault'
import { EmptyCell } from 'shared/ui/Table'
import { type ContactStatistic } from 'entities/ContactStatistic'
import { type IParamsGetBroadcastContacts } from 'entities/Broadcast/api/types'
import { Inbox } from 'entities/Inbox/model/Inbox'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type IOpenContactsDetails } from 'widgets/ContactsDetails'
import styles from './styles.module.scss'

const FailedReasonCell = ({ row }: { row: IRow<ContactStatistic> }) => {
  if (row.message_failed_reason) {
    return <div className={styles.reason}>{parse(row.message_failed_reason)}</div>
  }
  return <EmptyCell />
}

const SendFromCell = ({ row }: { row: IRow<ContactStatistic> }) => {
  const { inboxesStore, numbersStore } = useEntitiesContext()
  const inbox = inboxesStore.getItem(row.team_id)
  const number = numbersStore.getItem(row.number_id)

  const props: INameValueRecordProps = {
    name: 'Contact owner',
    icon: 'inboxes2',
  }

  if (inbox instanceof Inbox && number) {
    props.name = inbox.name
    props.value = number.formatted_number
  }

  if (inbox instanceof Inbox && inbox.isRCS) {
    props.name = inbox.name
    props.icon = 'rcs'
  }

  return <NameValueRecord {...props} />
}

const getColumnsConfig = (onOpenContactsDetails: IOpenContactsDetails) => ({
  full_name: {
    field: 'full_name',
    name: 'Name',
    isSortable: false,
    renderRowCell: (row, _, config) => (
      <div className={styles.contactColumn}>
        <div className={styles.contactColumnName}>
          {getCellForNameDefault({
            fullName: row.full_name || '',
            avatarInfo: row.avatarInfo,
          })}
        </div>
        {row.conversation_id !== null && (
          <Tooltip label='Conversation' placement='top'>
            <IconButton
              icon='chatRead'
              variant='icon'
              color='tertiary'
              transparentInactive
              className={config?.hidden}
              disabled={onOpenContactsDetails.disabled}
              onClick={(event) => {
                event.stopPropagation()
                onOpenContactsDetails.open({
                  contactId: row.id,
                  inboxId: row.team_id,
                })
              }}
              ariaLabel='ContactRow_sendMessage'
            />
          </Tooltip>
        )}
      </div>
    ),
  } as IColumn<ContactStatistic>,
  number: {
    field: 'number',
    name: 'Phone',
    isSortable: false,
    renderRowCell: (row) => row.number && getLabelAsNumberInternationalFormat(row.number),
  } as IColumn<ContactStatistic>,
  email: {
    field: 'email',
    name: 'Email',
    isSortable: false,
  } as IColumn<ContactStatistic>,
  sendFrom: {
    field: 'sendFrom',
    name: 'Send from',
    isSortable: false,
    renderRowCell: (row) => <SendFromCell row={row} />,
  } as IColumn<ContactStatistic>,
  message_sent_at: {
    field: 'message_sent_at',
    isSortable: false,
    name: 'Send time',
    renderRowCell: (row) =>
      row.message_sent_at &&
      uiStore.dayjs(row.message_sent_at).format(DayjsFormats.fullWithAtDash2),
  } as IColumn<ContactStatistic>,
  message_failed_reason: {
    field: 'message_failed_reason',
    name: 'Failed reason',
    renderRowCell: (row) => <FailedReasonCell row={row} />,
  } as IColumn<ContactStatistic>,
  message_replied_at: {
    field: 'message_replied_at',
    name: 'Replied at',
    renderRowCell: (row) =>
      row.message_replied_at &&
      uiStore.dayjs(row.message_replied_at).format(DayjsFormats.fullWithAtDash2),
  } as IColumn<ContactStatistic>,
  message_opted_out_at: {
    field: 'message_opted_out_at',
    name: 'Opt-out at',
    renderRowCell: (row) =>
      row.message_opted_out_at &&
      uiStore.dayjs(row.message_opted_out_at).format(DayjsFormats.fullWithAtDash2),
  } as IColumn<ContactStatistic>,
  short_url_last_clicked_at: {
    field: 'short_url_last_clicked_at',
    name: 'Clicked at',
    renderRowCell: (row) =>
      row.short_url_last_clicked_at &&
      uiStore.dayjs(row.short_url_last_clicked_at).format(DayjsFormats.fullWithAtDash2),
  } as IColumn<ContactStatistic>,
  last_converted_at: {
    field: 'last_converted_at',
    name: 'Converted at',
    renderRowCell: (row) =>
      row.last_converted_at &&
      uiStore.dayjs(row.last_converted_at).format(DayjsFormats.fullWithAtDash2),
  } as IColumn<ContactStatistic>,
})

export const useContactStatisticColumns = (
  filter: IParamsGetBroadcastContacts['filter'],
  onOpenContactsDetails: IOpenContactsDetails
): IColumn<ContactStatistic>[] => {
  const columnsMap = getColumnsConfig(onOpenContactsDetails)

  const commonColumns = [
    columnsMap.full_name,
    columnsMap.number,
    columnsMap.email,
    columnsMap.sendFrom,
  ]

  commonColumns.push(columnsMap.message_sent_at)

  if (filter === 'failed') {
    return [...commonColumns, columnsMap.message_failed_reason]
  }
  if (filter === 'opted_out') {
    return [...commonColumns, columnsMap.message_opted_out_at]
  }
  if (filter === 'clicked') {
    return [...commonColumns, columnsMap.short_url_last_clicked_at]
  }
  if (filter === 'conversions') {
    return [...commonColumns, columnsMap.last_converted_at]
  }
  return commonColumns
}
